import { useCallback } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import { extensionOf } from '~/components/tasks/detail/evidenceFiles';
import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import {
  tasksEvidenceCreate,
  tasksLogsCreate,
  tasksPartialUpdate,
  tasksRetrieve,
} from '~/data/generated/endpoints/tasks/tasks';
import type {
  FileTypeEnum,
  PatchedTaskUpdateRequest,
  TaskDetail,
  TaskLogCreateRequest,
} from '~/data/generated/model';
import { TasksRetrieveResponse } from '~/data/generated/zod/tasks/tasks.zod';
import { taskDetailKey, useRevalidateTasks } from '~/data/hooks/taskKeys';
import { parseApiResponse } from '~/data/parseApiResponse';
import { uploadAttachment } from '~/data/uploadAttachment';

export function useTask(taskId: string | null | undefined) {
  const enabled = useTasksEnabled();
  const { data, error, isLoading, mutate } = useSWR(
    enabled && taskId ? taskDetailKey(taskId) : null,
    async ([, , id]) =>
      parseApiResponse(TasksRetrieveResponse, (await tasksRetrieve(id)).data, 'TaskDetail'),
  );
  return { task: data, error, isLoading, mutate };
}

function fileTypeOf(file: File): FileTypeEnum {
  const extension = extensionOf(file.name);
  if (['jpg', 'jpeg', 'png'].includes(extension)) return 'image';
  if (['csv', 'xls', 'xlsx'].includes(extension)) return 'spreadsheet';
  return 'pdf';
}

export function useTaskActions(taskId: string) {
  const { mutate } = useSWRConfig();
  const revalidate = useRevalidateTasks();

  const run = useCallback(
    async (write: () => Promise<{ data: TaskDetail }>): Promise<TaskDetail> => {
      const { data } = await write();
      await mutate(taskDetailKey(taskId), data, { revalidate: false });
      await revalidate();
      return data;
    },
    [mutate, revalidate, taskId],
  );

  const updateTask = useCallback(
    (body: PatchedTaskUpdateRequest) => run(() => tasksPartialUpdate(taskId, body)),
    [run, taskId],
  );

  const addLog = useCallback(
    (body: TaskLogCreateRequest) => run(() => tasksLogsCreate(taskId, body)),
    [run, taskId],
  );

  const addEvidence = useCallback(
    (file: File, logId?: string) =>
      run(async () => {
        const attachment = await uploadAttachment({
          file,
          name: file.name,
          fileType: fileTypeOf(file),
        });
        return tasksEvidenceCreate(taskId, { attachmentId: attachment.id, logId });
      }),
    [run, taskId],
  );

  const setStatus = useCallback(
    (statusId: string) => run(() => tasksPartialUpdate(taskId, { statusId })),
    [run, taskId],
  );

  return { updateTask, addLog, addEvidence, setStatus };
}
